import React, { useEffect, useState } from 'react';
import { Button, Drawer } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

export interface ISidebarContentLayoutProps {
  /** Heading shown above the sidebar nav (desktop/tablet) and in the mobile top bar/Drawer. */
  title: React.ReactNode;
  /** Fully-rendered nav item list -- rendered as-is both in the fixed
   * desktop/tablet sidebar and inside the mobile Drawer, so each caller
   * keeps its own nav-item visual style (pill highlight, flat highlight,
   * etc.) untouched by this component. */
  sidebar: React.ReactNode;
  /** Whichever section key is currently selected -- used only to detect a
   * navigation and auto-close the mobile Drawer, the same "select and it
   * closes" behavior AppHeader's own hamburger menu already has. */
  activeKey: string;
  /** The active section's content. */
  children: React.ReactNode;
  sidebarWidth?: number;
  wrapperStyle?: React.CSSProperties;
  sidebarStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
}

/**
 * Shared "sidebar nav + content" page shell for Settings/Admin (and any
 * future screen with the same shape): a fixed-width sidebar next to a
 * content panel on desktop/tablet, collapsing on mobile (isMobile from
 * useAppBreakpoint) into a top menu button that opens the sidebar in an
 * antd Drawer -- same pattern/component AppHeader already uses for its own
 * nav. Deliberately unopinionated about borders/radius/shadow (see the
 * *Style props) so each caller's existing outer chrome carries over as-is;
 * this only owns the responsive collapse mechanism.
 */
const SidebarContentLayout: React.FC<ISidebarContentLayoutProps> = ({
  title,
  sidebar,
  activeKey,
  children,
  sidebarWidth = 260,
  wrapperStyle,
  sidebarStyle,
  contentStyle,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [activeKey]);

  if (isMobile) {
    return (
      <div style={wrapperStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 0' }}>
          {title}
          <Button
            type="text"
            icon={<MenuOutlined style={{ fontSize: 20 }} />}
            aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_MENU_ARIA_LABEL)}
            onClick={() => setDrawerOpen(true)}
          />
        </div>
        <Drawer title={title} placement="left" onClose={() => setDrawerOpen(false)} open={drawerOpen} width={280}>
          {sidebar}
        </Drawer>
        <div style={contentStyle}>{children}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', ...wrapperStyle }}>
      <div style={{ width: sidebarWidth, flexShrink: 0, ...sidebarStyle }}>
        {title}
        {sidebar}
      </div>
      <div style={{ flex: 1, minWidth: 0, ...contentStyle }}>{children}</div>
    </div>
  );
};

export default SidebarContentLayout;
